/**
 * Projects module API helpers (JobNumber-backed list/detail/create/patch).
 *
 * Auth: session + CSRF via ``apiRequest``.
 * View, create, and edit require manager/executive.
 */

import { API_PATHS } from "../../config";
import { apiRequest } from "./axios";

/**
 * Fetch projects for the Projects module landing page.
 *
 * @param {Object} params - Project list filters.
 * @param {boolean} [params.activeOnly=true] - Date-window active jobs via
 *   ``get_active_jobs`` / ``is_job_date_window_active``. Each row also has
 *   serializer ``active`` (same rules).
 * @param {string|null} [params.date=null] - Optional YYYY-MM-DD active date filter.
 * @returns {Promise<Array<Object>>}
 */
export async function getProjects({ activeOnly = true, date = null } = {}) {
  const params = new URLSearchParams();
  params.append("active", activeOnly.toString());
  if (date) {
    params.append("date", date);
  }

  return apiRequest(`${API_PATHS.PROJECTS.LIST}?${params.toString()}`, null, {
    method: "GET",
  });
}

/**
 * Fetch one project by the backing JobNumber ID.
 *
 * @param {string|number} projectId - JobNumber primary key.
 * @returns {Promise<Object>}
 */
export async function getProject(projectId) {
  return apiRequest(API_PATHS.PROJECTS.DETAIL(projectId), null, {
    method: "GET",
  });
}

/**
 * Create a project (JobNumber) from the Projects add form.
 *
 * @param {Object} payload - Same field shape as updateProject.
 * @returns {Promise<Object>} Created project detail row.
 */
export async function createProject(payload) {
  return apiRequest(API_PATHS.PROJECTS.LIST, payload, {
    method: "POST",
  });
}

/**
 * Update a project via ``PATCH /api/projects/<id>/`` (detail serializer response).
 *
 * @param {string|number} projectId - JobNumber primary key.
 * @param {Object} payload - Approved project fields.
 * @returns {Promise<Object>} Updated project detail row.
 */
export async function updateProject(projectId, payload) {
  return apiRequest(API_PATHS.PROJECTS.DETAIL(projectId), payload, {
    method: "PATCH",
  });
}

/**
 * Batch-fetch Projects Home card ops summaries (concrete today/7d + payroll week).
 *
 * @param {Object} params
 * @param {Array<number|string>} params.jobIds - JobNumber primary keys.
 * @param {Object} [config] - Optional axios config (e.g. ``signal``).
 * @returns {Promise<{
 *   weekEnding: string|null,
 *   weekStarting: string|null,
 *   summaries: Object<string, {
 *     concreteToday: number,
 *     concrete7d: number,
 *     laborPayrollWeekHours: number,
 *     payrollWeekEnding: string|null,
 *   }>
 * }>}
 */
export async function getProjectOpsSummaries({ jobIds = [] } = {}, config = {}) {
  const params = new URLSearchParams();
  if (jobIds.length) {
    params.set("jobs", jobIds.map(String).join(","));
  }
  const query = params.toString();
  const url = `${API_PATHS.PROJECTS.OPS_SUMMARIES}${query ? `?${query}` : ""}`;
  return apiRequest(url, null, { method: "GET", ...config });
}
